/* eslint-disable */
/**
 * The install method for the Live Chat Vue plugin.
 * Supports Crisp, Tawk.to, and Zendesk — determined dynamically by platform_configuration or env variables.
 *
 * @param {import('vue').App} app
 * @param {object} [options]
 * @param {boolean|string} [options.chatEnabled]
 * @param {string} [options.provider] - 'none' | 'crisp' | 'tawk' | 'zendesk'
 * @param {string} [options.talkToSrc] - The script URL for Tawk.to
 * @param {string} [options.crispWebsiteId] - The Crisp Website ID
 * @param {string} [options.zendeskKey] - The Zendesk snippet key
 */
let appInstance = null;

export function installLiveChat(app, options = {}) {
	appInstance = app;
	if (options && (options.provider || options.chatEnabled !== undefined)) {
		initLiveChat(options);
	}
}

/**
 * Dynamically initializes and embeds the configured live chat widget script.
 * @param {object} options
 */
export function initLiveChat(options = {}) {
	const chatEnabled = options.chatEnabled ?? process.env.VUE_APP_CHAT_ENABLED;
	const provider = options.provider ?? 'none';
	const hideBubble = options.hideBubble === true || options.hideBubble === 'true' || options.hideBubble === '1' || options.hideBubble === 1;
	const talkToSrc = options.talkToSrc || process.env.VUE_APP_TALK_TO;
	const crispWebsiteId = options.crispWebsiteId || process.env.VUE_APP_CRISP_WEBSITE_ID;
	const zendeskKey = options.zendeskKey || process.env.VUE_APP_ZENDESK_KEY;
	const hooryWebsiteToken = options.hooryWebsiteToken || process.env.VUE_APP_HOORY_WEBSITE_TOKEN;
	const hooryBaseUrl = options.hooryBaseUrl || process.env.VUE_APP_HOORY_BASE_URL || 'https://app.hoory.com';

	const isEnabled = chatEnabled === true || chatEnabled === 'true' || chatEnabled === '1' || chatEnabled === 1;

	if (!isEnabled || provider === 'none') {
		if (appInstance) appInstance.config.globalProperties.$liveChatProvider = null;
		window.$liveChatProvider = null;
		return;
	}

	if (document.querySelector('script[data-live-chat-loaded="1"]')) {
		return;
	}

	switch (provider) {
		case 'crisp':
			if (crispWebsiteId) {
				initCrisp(crispWebsiteId, hideBubble);
			} else {
				handleMissingCredentials(provider);
			}
			break;
		case 'tawk':
			if (talkToSrc) {
				initTawk(talkToSrc, hideBubble);
			} else {
				handleMissingCredentials(provider);
			}
			break;
		case 'zendesk':
			if (zendeskKey) {
				initZendesk(zendeskKey, hideBubble);
			} else {
				handleMissingCredentials(provider);
			}
			break;
		case 'hoory':
			if (hooryWebsiteToken) {
				initHoory(hooryWebsiteToken, hooryBaseUrl, hideBubble);
			} else {
				handleMissingCredentials(provider);
			}
			break;
		default:
			if (appInstance) appInstance.config.globalProperties.$liveChatProvider = null;
			window.$liveChatProvider = null;
			console.warn(`Unknown live chat provider: ${provider}`);
	}
}

/**
 * Handles the fallback scenario when a provider is selected but credentials are missing.
 * @param {import('vue').App} app
 * @param {string} provider
 */
function handleMissingCredentials(provider) {
	if (appInstance) appInstance.config.globalProperties.$liveChatProvider = null;
	window.$liveChatProvider = null;
	console.warn(`Live chat enabled but credentials missing for active provider: ${provider}`);
}

/**
 * Initializes and embeds Crisp Chat.
 * @param {import('vue').App} app
 * @param {string} websiteId
 * @param {boolean} [hideBubble=false]
 */
function initCrisp(websiteId, hideBubble = false) {
	const isRTL = document.documentElement.getAttribute('dir') === 'rtl';

	window.$crisp = [];
	window.CRISP_WEBSITE_ID = websiteId;

	window.$crisp.push(["config", "position:reverse", [isRTL]]);
	window.$crisp.push(["config", "container:index", [9996]]);

	if (hideBubble) {
		window.$crisp.push(["do", "chat:hide"]);
		window.$crisp.push(["on", "chat:closed", () => {
			window.$crisp.push(["do", "chat:hide"]);
		}]);
	}

	const script = document.createElement('script');
	script.src = 'https://client.crisp.chat/l.js';
	script.async = true;
	script.setAttribute('data-live-chat-loaded', '1');
	document.getElementsByTagName('head')[0].appendChild(script);

	if (appInstance) appInstance.config.globalProperties.$liveChatProvider = 'crisp';
	window.$liveChatProvider = 'crisp';
	console.log('Crisp chat loaded.');
}

/**
 * Initializes and embeds Tawk.to.
 * @param {import('vue').App} app
 * @param {string} src
 * @param {boolean} [hideBubble=false]
 */
function initTawk(src, hideBubble = false) {
	window.Tawk_API = window.Tawk_API || {};
	window.Tawk_LoadStart = new Date();
	const isRTL = document.documentElement.getAttribute('dir') === 'rtl';

	window.Tawk_API.customStyle = {
		zIndex: 9996,
		visibility: {
			mobile: {
				position: isRTL ? 'bl' : 'br',
				xOffset: '30px',
				yOffset: '90px'
			},
			desktop: {
				position: isRTL ? 'bl' : 'br',
				xOffset: '30px',
				yOffset: '50px'
			}
		}
	};

	window.Tawk_API.onLoad = () => {
		if (hideBubble) {
			window.Tawk_API?.hideWidget?.();
		} else {
			window.Tawk_API?.showWidget?.();
		}
	};

	window.Tawk_API.onChatMaximized = () => {
		const betslipModal = document.querySelector('.eventime_mobal_content.form-popup');
		if (betslipModal) {
			betslipModal.style.zIndex = 249;
		}
	};

	window.Tawk_API.onChatMinimized = () => {
		const betslipModal = document.querySelector('.eventime_mobal_content.form-popup');
		if (betslipModal) {
			betslipModal.style.zIndex = 9997;
		}
		cleanupAfterTawk();
		if (hideBubble && typeof window.Tawk_API?.hideWidget === 'function') {
			window.Tawk_API.hideWidget();
		}
	};

	const script = document.createElement('script');
	const firstScript = document.getElementsByTagName('script')[0] || document.head;
	script.async = true;
	script.src = src;
	script.setAttribute('crossorigin', '*');
	script.setAttribute('data-live-chat-loaded', '1');
	if (firstScript.parentNode) {
		firstScript.parentNode.insertBefore(script, firstScript);
	} else {
		document.head.appendChild(script);
	}

	if (appInstance) appInstance.config.globalProperties.$liveChatProvider = 'tawk';
	window.$liveChatProvider = 'tawk';
	console.log('Tawk.to chat loaded.');
}

/**
 * Cleans up global inline styles added/modified by Tawk.to when minimizing the chat widget.
 */
function cleanupAfterTawk() {
	const body = document.body;
	const html = document.documentElement;

	const propertiesToRemove = [
		'position', 'top', 'left', 'right', 'width', 'height',
		'min-height', 'max-height', 'max-width', 'overflow'
	];
	propertiesToRemove.forEach(prop => body.style.removeProperty(prop));

	html.style.removeProperty('overflow');
	html.style.removeProperty('height');

	if (location.hash === '#max-widget') {
		history.replaceState(null, document.title, location.pathname + location.search);
	}

	const y = window.scrollY || window.pageYOffset || 0;
	requestAnimationFrame(() => {
		window.scrollTo(0, y + 1);
		requestAnimationFrame(() => window.scrollTo(0, y));
	});

	setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
}

/**
 * Initializes and embeds Zendesk Web Widget.
 * @param {import('vue').App} app
 * @param {string} key
 * @param {boolean} [hideBubble=false]
 */
function initZendesk(key, hideBubble = false) {
	const script = document.createElement('script');
	script.id = 'ze-snippet';
	script.async = true;
	script.setAttribute('data-live-chat-loaded', '1');
	script.src = `https://static.zdassets.com/ekr/snippet.js?key=${key}`;

	script.onload = () => {
		if (window.zE) {
			const isRTL = document.documentElement.getAttribute('dir') === 'rtl';

			window.zESettings = {
				webWidget: {
					zIndex: 9996,
					offset: { horizontal: '20px', vertical: '50px' },
					position: { horizontal: isRTL ? 'left' : 'right', vertical: 'bottom' },
					mobile: {
						offset: { horizontal: '100px', vertical: '100px' }
					}
				}
			};

			if (hideBubble) {
				window.zE('webWidget', 'hide');
			}

			window.zE('webWidget:on', 'close', () => {
				if (hideBubble) {
					window.zE('webWidget', 'hide');
				}
			});
		}
	};

	document.head.appendChild(script);
	if (appInstance) appInstance.config.globalProperties.$liveChatProvider = 'zendesk';
	window.$liveChatProvider = 'zendesk';
	console.log('Zendesk chat loaded.');
}

/**
 * Initializes and embeds Hoory AI Live Chat.
 * @param {string} websiteToken
 * @param {string} [baseUrl='https://app.hoory.com']
 * @param {boolean} [hideBubble=false]
 */
function initHoory(websiteToken, baseUrl = 'https://app.hoory.com', hideBubble = false) {
	const isRTL = document.documentElement.getAttribute('dir') === 'rtl';

	window.hoorySettings = {
		hideMessageBubble: hideBubble,
		position: isRTL ? 'left' : 'right',
	};

	const script = document.createElement('script');
	script.src = `${baseUrl}/packs/js/sdk.js`;
	script.async = true;
	script.defer = true;
	script.setAttribute('data-live-chat-loaded', '1');

	script.onload = () => {
		if (window.hoorySDK) {
			window.hoorySDK.run({
				websiteToken: websiteToken,
				baseUrl: baseUrl,
			});
		}
	};

	document.head.appendChild(script);

	if (appInstance) appInstance.config.globalProperties.$liveChatProvider = 'hoory';
	window.$liveChatProvider = 'hoory';
	console.log('Hoory live chat loaded.');
}

/**
 * Toggles the visibility of the active live chat widget.
 * @param {boolean} shouldHideBubble - Whether the bubble is configured to be hidden when closed.
 */
export function openLiveChat(shouldHideBubble = false) {
	if (window.$crisp) {
		if (shouldHideBubble) {
			window.$crisp.push(['do', 'chat:show']);
		}
		if (typeof window.$crisp.is === 'function' && window.$crisp.is('chat:opened')) {
			window.$crisp.push(['do', 'chat:close']);
			if (shouldHideBubble) {
				window.$crisp.push(['do', 'chat:hide']);
			}
		} else {
			window.$crisp.push(['do', 'chat:open']);
		}
	} else if (window.Tawk_API) {
		if (shouldHideBubble && typeof window.Tawk_API.showWidget === 'function') {
			window.Tawk_API.showWidget();
		}

		if (typeof window.Tawk_API.isChatMaximized === 'function' && window.Tawk_API.isChatMaximized()) {
			window.Tawk_API.minimize?.();
			if (shouldHideBubble && typeof window.Tawk_API.hideWidget === 'function') {
				window.Tawk_API.hideWidget();
			}
		} else {
			if (typeof window.Tawk_API.maximize === 'function') {
				window.Tawk_API.maximize();
			} else {
				window.Tawk_API?.toggle?.();
			}
		}
	} else if (window.zE && typeof window.zE === 'function') {
		if (shouldHideBubble) {
			window.zE('webWidget', 'show');
		}
		window.zE('webWidget', 'toggle');
	} else if (window.$hoory && typeof window.$hoory.toggle === 'function') {
		if (shouldHideBubble && !window.$hoory.isOpen && typeof window.$hoory.toggleBubbleVisibility === 'function') {
			window.$hoory.toggleBubbleVisibility('show');
		}
		if (window.$hoory.isOpen) {
			window.$hoory.toggle('close');
			if (shouldHideBubble && typeof window.$hoory.toggleBubbleVisibility === 'function') {
				window.$hoory.toggleBubbleVisibility('hide');
			}
		} else {
			window.$hoory.toggle('open');
		}
	} else if (window.hoorySDK || document.querySelector('script[src*="packs/js/sdk.js"]')) {
		window.addEventListener('hoory:ready', () => {
			if (window.$hoory && typeof window.$hoory.toggle === 'function') {
				if (shouldHideBubble && typeof window.$hoory.toggleBubbleVisibility === 'function') {
					window.$hoory.toggleBubbleVisibility('show');
				}
				window.$hoory.toggle('open');
			}
		}, { once: true });
	}
}